import { z } from 'zod';

import {
  SAVED_VIEW_ROOT_MODES,
  VIEW_EDGE_KINDS,
  VIEW_NODE_KINDS,
  VIEW_REDACTION_REASONS,
  VIEW_SEMANTIC_LEVELS,
} from '../constants/enums';

const lensDslWhereSchema = z.record(z.string(), z.unknown()).default({});

const lensDslQueryClauseSchema = z.object({
  primitive: z.string().min(1),
  where: lensDslWhereSchema.optional(),
});

export const lensDslSchema = z
  .object({
    dsl_version: z.literal('lens/v1'),
    metadata: z.object({
      name: z.string().min(1),
      description: z.string().optional(),
    }),
    root_types: z.array(z.string().min(1)).min(1),
    query: z
      .object({
        include: z.array(lensDslQueryClauseSchema).default([]),
        exclude: z.array(lensDslQueryClauseSchema).default([]),
      })
      .default({ include: [], exclude: [] }),
    semantic_levels: z.array(z.enum(VIEW_SEMANTIC_LEVELS)).default(['L1']),
    components: z.array(
      z.object({
        region: z.string().min(1),
        component: z.string().min(1),
        source: z.string().min(1),
      }),
    ),
    panels: z
      .array(
        z.object({
          slot: z.string().min(1),
          component: z.string().min(1),
          source: z.string().min(1),
        }),
      )
      .default([]),
    action_intents: z
      .array(
        z.object({
          id: z.string().min(1),
          capability: z.string().min(1),
        }),
      )
      .default([]),
    visual_encodings: z
      .array(
        z.object({
          concept: z.string().min(1),
          shape: z.string().min(1),
        }),
      )
      .default([]),
    layout: z
      .object({
        default_engine: z.enum(['cluster-grid', 'dagre', 'force']).default('cluster-grid'),
      })
      .default({ default_engine: 'cluster-grid' }),
    limits: z
      .object({
        max_nodes: z.number().int().min(1).max(500).default(150),
        max_edges: z.number().int().min(0).max(1_000).default(300),
      })
      .default({ max_nodes: 150, max_edges: 300 }),
    redaction_display_policy: z.string().min(1).default('safe_counts'),
  })
  .passthrough();
export type LensDsl = z.infer<typeof lensDslSchema>;

export interface LensDslValidationError {
  path: string;
  message: string;
}

export type LensDslValidationResult =
  | { ok: true; spec_hash: string }
  | { ok: false; errors: LensDslValidationError[] };

const FORBIDDEN_LENS_DSL_KEYS = new Set([
  'sql',
  'javascript',
  'tool',
  'credential',
  'secret',
  'approval_mode',
  'policy_override',
  'prompt_template',
  'direct_mutation',
  'raw_event_insert',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function appendForbiddenKeyErrors(
  value: unknown,
  path: string,
  errors: LensDslValidationError[],
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendForbiddenKeyErrors(item, path ? `${path}.${index}` : String(index), errors);
    });
    return;
  }
  if (!isRecord(value)) return;

  for (const [key, child] of Object.entries(value)) {
    const childPath = path ? `${path}.${key}` : key;
    if (FORBIDDEN_LENS_DSL_KEYS.has(key)) {
      errors.push({
        path: childPath,
        message: `Forbidden Lens DSL key: ${key}`,
      });
    }
    appendForbiddenKeyErrors(child, childPath, errors);
  }
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

export function hashLensDsl(value: unknown): string {
  const text = stableStringify(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `lens_${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function validateLensDsl(value: unknown): LensDslValidationResult {
  const forbiddenErrors: LensDslValidationError[] = [];
  appendForbiddenKeyErrors(value, '', forbiddenErrors);
  if (forbiddenErrors.length > 0) {
    return { ok: false, errors: forbiddenErrors };
  }

  const parsed = lensDslSchema.safeParse(value);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      })),
    };
  }
  return { ok: true, spec_hash: hashLensDsl(parsed.data) };
}

export const explorerRenderRequestSchema = z.object({
  root: z
    .object({
      mode: z.enum(SAVED_VIEW_ROOT_MODES),
      context_ids: z.array(z.string().min(1)).default([]),
      selector: z.string().nullable().optional(),
    })
    .superRefine((root, ctx) => {
      if (root.mode === 'fixed' && root.context_ids.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['context_ids'],
          message: 'fixed root requires at least one context id',
        });
      }
    }),
  lens_id: z.string().min(1),
  lens_version: z.string().min(1).optional(),
  saved_view_id: z.string().min(1).optional(),
  semantic_level: z.enum(VIEW_SEMANTIC_LEVELS).default('L1'),
  time_range: z
    .object({
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
    })
    .optional(),
  params: z.record(z.string(), z.unknown()).default({}),
  layout_overrides: z.record(z.string(), z.unknown()).default({}),
});
export type ExplorerRenderRequest = z.infer<typeof explorerRenderRequestSchema>;

const viewPacketMetaSchema = z.object({
  root_context_ids: z.array(z.string()),
  lens_id: z.string(),
  lens_version: z.string(),
  semantic_level: z.enum(VIEW_SEMANTIC_LEVELS),
  node_count: z.number().int().min(0),
  edge_count: z.number().int().min(0),
  truncated: z.boolean(),
  budget: z.object({
    max_nodes: z.number().int().min(1),
    max_edges: z.number().int().min(0),
  }),
  generated_at: z.string().min(1),
  source_watermark: z.string().min(1),
  access_fingerprint: z.string().min(1),
});

const viewPacketNodeSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(VIEW_NODE_KINDS),
  label: z.string(),
  context_id: z.string().nullable().optional(),
  cluster_id: z.string().nullable().optional(),
  position: z.object({ x: z.number(), y: z.number() }),
  status: z.object({ kind: z.string(), label: z.string() }),
  meta: z.record(z.string(), z.unknown()).default({}),
});

const viewPacketEdgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  kind: z.enum(VIEW_EDGE_KINDS),
  label: z.string(),
  meta: z.record(z.string(), z.unknown()).default({}),
});

const viewPacketRedactionSchema = z.object({
  scope: z.enum(['node', 'edge', 'panel', 'action']),
  reason: z.enum(VIEW_REDACTION_REASONS),
  disclosure: z.enum(['none', 'existence', 'aggregate']),
});

export const viewPacketV3Schema = z.object({
  view_packet_schema: z.string().default('view_packet/v3'),
  view_packet_version: z.string().default('3.0'),
  meta: viewPacketMetaSchema,
  nodes: z.array(viewPacketNodeSchema).default([]),
  edges: z.array(viewPacketEdgeSchema).default([]),
  panels: z.array(z.record(z.string(), z.unknown())).default([]),
  actions: z.array(z.record(z.string(), z.unknown())).default([]),
  redactions: z.array(viewPacketRedactionSchema).default([]),
  overflow: z.object({
    hidden_node_count: z.number().int().min(0),
    hidden_edge_count: z.number().int().min(0),
    reason: z.enum(VIEW_REDACTION_REASONS).nullable(),
  }),
  regions: z.record(z.string(), z.unknown()).default({}),
  available_actions: z.array(z.record(z.string(), z.unknown())).default([]),
  states: z.record(z.string(), z.unknown()).default({}),
});
export type ViewPacketV3 = z.infer<typeof viewPacketV3Schema>;
